import React from 'react';
import { styled } from 'styled-components';
import { Send, CheckCheck, Eye, X, MessageSquare, Target } from 'lucide-react';

interface TemplateMetricsProps {
  metrics: {
    attempted: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    replied: number;
  };
}

const TemplateMetrics: React.FC<TemplateMetricsProps> = ({ metrics }) => {
  const { attempted, sent, delivered, read, failed, replied } = metrics;
  
  // Calculate percentages
  const deliveredRate = sent > 0 ? ((delivered / sent) * 100).toFixed(1) : '0';
  const readRate = delivered > 0 ? ((read / delivered) * 100).toFixed(1) : '0';
  const replyRate = sent > 0 ? ((replied / sent) * 100).toFixed(1) : '0';
  const successRate = attempted > 0 ? ((sent / attempted) * 100).toFixed(1) : '0';

  return (
    <MetricsWrapper>
      <MetricsHeader>
       <HeaderTitle>📈 Template Performance Overview</HeaderTitle>
<HeaderSubtitle>See how your messages are performing across all campaigns</HeaderSubtitle>
      </MetricsHeader>

      <MetricsGrid>
        <MetricBox>
          <IconWrapper $color="#6366f1">
            <Target size={20} />
          </IconWrapper>
          <MetricContent>
            <MetricLabel>Attempted</MetricLabel>
            <MetricValue>{attempted.toLocaleString()}</MetricValue>
          </MetricContent>
        </MetricBox>

        <MetricBox>
          <IconWrapper $color="#22c55e">
            <Send size={20} />
          </IconWrapper>
          <MetricContent>
            <MetricLabel>Sent</MetricLabel>
            <MetricValue>{sent.toLocaleString()}</MetricValue>
            <MetricRate $color="#22c55e">Success: {successRate}%</MetricRate>
          </MetricContent>
        </MetricBox>

        <MetricBox>
          <IconWrapper $color="#10b981">
            <CheckCheck size={20} />
          </IconWrapper>
          <MetricContent>
            <MetricLabel>Delivered</MetricLabel>
            <MetricValue>{delivered.toLocaleString()}</MetricValue>
            <MetricRate $color="#10b981">Rate: {deliveredRate}%</MetricRate>
          </MetricContent>
        </MetricBox>

        <MetricBox>
          <IconWrapper $color="#f59e0b">
            <Eye size={20} />
          </IconWrapper>
          <MetricContent>
            <MetricLabel>Read</MetricLabel>
            <MetricValue>{read.toLocaleString()}</MetricValue>
            <MetricRate $color="#f59e0b">Rate: {readRate}%</MetricRate>
          </MetricContent>
        </MetricBox>

        <MetricBox>
          <IconWrapper $color="#ef4444">
            <X size={20} />
          </IconWrapper>
          <MetricContent>
            <MetricLabel>Failed</MetricLabel>
            <MetricValue>{failed.toLocaleString()}</MetricValue>
          </MetricContent>
        </MetricBox>

        <MetricBox>
          <IconWrapper $color="#8b5cf6">
            <MessageSquare size={20} />
          </IconWrapper>
          <MetricContent>
            <MetricLabel>Replied</MetricLabel>
            <MetricValue>{replied.toLocaleString()}</MetricValue>
            <MetricRate $color="#8b5cf6">Rate: {replyRate}%</MetricRate>
          </MetricContent>
        </MetricBox>
      </MetricsGrid>

      <MetricsFooter>
        <FooterNote>Data reflects template performance across all campaigns. Matrics are regularly updated</FooterNote>
      </MetricsFooter>
    </MetricsWrapper>
  );
};

const MetricsWrapper = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const MetricsHeader = styled.div`
  margin-bottom: 20px;
  text-align: center;
`;

const HeaderTitle = styled.h5`
  font-size: 20px;
  color: white;
  margin: 0 0 8px 0;
  font-weight: 700;
  font-family: "Gilroy-Bold";
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const HeaderSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-family: "Gilroy-Medium";
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const MetricBox = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    background: white;
  }
`;

const IconWrapper = styled.div<{ $color: string }>`
  background: ${props => props.$color};
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px ${props => props.$color}40;
`;

const MetricContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const MetricLabel = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-bottom: 4px;
  font-family: "Gilroy-Medium";
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  font-family: "Gilroy-Bold";
  line-height: 1.2;
`;

const MetricRate = styled.div<{ $color: string }>`
  font-size: 12px;
  color: ${props => props.$color};
  margin-top: 4px;
  font-family: "Gilroy-SemiBold";
  font-weight: 600;
`;

const MetricsFooter = styled.div`
  text-align: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const FooterNote = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  font-family: "Gilroy-Medium";
  font-style: italic;
`;

export default TemplateMetrics;